import { memo, PropsWithChildren, useCallback, useEffect } from 'react';

import { Box, Typography } from '@mui/material';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import * as Sentry from '@sentry/react';

import { defaultMapOptions, googleMapID } from '@/config';
import { MapContextProvider, useMapContext } from '@/contexts';
import { IPoint } from '@/models';

const containerStyle = {
  flex: 1,
  padding: '0px 20px',
};

export interface BaseMapProps extends PropsWithChildren {
  centerPoint?: IPoint;
}

const BaseMapComp = ({ centerPoint, children }: BaseMapProps) => {
  const { map, setMap } = useMapContext();
  const isLoaded = window.google;

  useEffect(() => {
    if (map && centerPoint) {
      map.setCenter({
        lat: centerPoint.coordinates[1],
        lng: centerPoint.coordinates[0],
      });
    }
  }, [map, centerPoint]);

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    // This is just an example of getting and using the map instance!!! don't just blindly copy!
    map.setOptions(defaultMapOptions);
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap();
  }, []);

  return isLoaded ? (
    <GoogleMap
      id={googleMapID}
      mapContainerStyle={containerStyle}
      {...defaultMapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {!!centerPoint && (
        <MarkerF
          position={{
            lat: centerPoint.coordinates[1],
            lng: centerPoint.coordinates[0],
          }}
        />
      )}
      {children}
    </GoogleMap>
  ) : (
    <></>
  );
};

// Shown in place of the map when the SDK fails. It states the problem instead of
// leaving an empty panel, because the boundary below turns a crash into a silent
// failure that nobody would otherwise report.
const MapUnavailable = () => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'grey.100',
      borderRadius: 1,
    }}
  >
    <Typography sx={{ color: 'grey.600', fontSize: '0.875rem' }}>
      Map unavailable. Try refreshing the page.
    </Typography>
  </Box>
);

// Errors thrown by the Maps SDK surface from <GoogleMap> itself, above anything
// rendered as its children, so the boundary has to sit here to catch them. Without
// it a single Maps failure reaches the root boundary in App.tsx and unmounts the
// entire portal - which is how the removal of the drawing library in Maps JS v3.65
// turned the Agencies screens into a blank page. Losing the map panel is degraded
// but usable; losing the whole admin portal is not.
const BaseMap = (props: BaseMapProps) => (
  <Sentry.ErrorBoundary fallback={<MapUnavailable />}>
    <MapContextProvider>
      <BaseMapComp {...props} />
    </MapContextProvider>
  </Sentry.ErrorBoundary>
);

export default memo(BaseMap);
